import type { Selection } from '@app/store/actions.js';
import { createAction } from '@reduxjs/toolkit';
import z from 'zod';

export type Exportable =
  | 'plannedRoute'
  | 'objects'
  | 'pictures'
  | 'drawingLines'
  | 'drawingAreas'
  | 'drawingPoints'
  | 'tracking'
  | 'import'
  | 'search';

export const ExportTargetSchema = z.enum([
  'download',
  'share',
  'gdrive',
  'dropbox',
  'garmin',
]);

export type ExportTarget = z.infer<typeof ExportTargetSchema>;

export const ExportTypeSchema = z.enum(['gpx', 'geojson', 'kml']);

export type ExportType = z.infer<typeof ExportTypeSchema>;

// Button labels per format. Typed as a full record so adding a member to
// ExportTypeSchema without a label is a compile error.
export const EXPORT_FORMAT_LABELS: Record<ExportType, string> = {
  gpx: 'GPX',
  geojson: 'GeoJSON',
  kml: 'KML',
};

/**
 * Whether to fill elevation into exported features: `none` leaves coordinates
 * as-is, `missing` fills only those lacking elevation, `all` overwrites every
 * elevation from the elevation API.
 */
export const ExportElevationSchema = z.enum(['none', 'missing', 'all']);

export type ExportElevation = z.infer<typeof ExportElevationSchema>;

export const exportMapFeatures = createAction<{
  exportables: Exportable[];
  type: ExportType;
  target: ExportTarget;
  name?: string;
  description?: string;
  activity?: string;
  /**
   * Fill elevation into exported points, lines and the planned route. `none`
   * (or absent) leaves coordinates untouched; polygons are always skipped
   * (elevation has no meaning for an area outline).
   */
  elevation?: ExportElevation;
  /**
   * Share through the companion recorder app rather than the browser's own share sheet — the only
   * way the file reaches Android's sheet under its real name and type.
   *
   * Decided while the target was being picked rather than here, because the browser's sheet opens
   * only on the user activation of the Export click: asking the recorder at export time would spend
   * that activation and leave the fallback share unable to open anything.
   */
  viaRecorder?: boolean;
  /**
   * Restrict the export to the single currently-selected map feature. When set,
   * every source contributes only the item this selection targets (a drawing
   * point/line/polygon, an object, a track, the planned route, or the lookup
   * result); sources the selection doesn't target contribute nothing.
   */
  only?: Selection;
}>('EXPORT_MAP_FEATURES');
